// Renders the hex world: one filled hexagon per region (coloured by owner), a
// capital marker, unit chips, target highlights, and a selection outline. Picking
// is pure math (pixel→axial), no cached pickmap needed.

import { Container, Graphics, Sprite, Text, ColorMatrixFilter } from 'pixi.js';
import type { Layers } from './layers';
import type { GameState, RegionId, RegionSnapshot, Voyage } from '../game/types';
import { NEUTRAL, BUILDING } from '../game/types';
import { axialToPixel, hexCorners, hexKey, hexNeighbors, pixelToAxial } from '../game/hex';
import { unitMaxHp, unitMoves, canStillAct, waterBodyAt, shoreWatersOf } from '../game/engine';
import type { SpriteStore } from './sprites';

const BUILDING_SPRITE = ['market', 'barracks', 'wall', 'harbor'] as const; // index === BuildingType
const BUILDING_BADGE = [0xf2c14e, 0xd05a5a, 0x9aa7b3, 0x3f7fd0] as const; // vector fallback colour per building

const NEUTRAL_COLOR = 0x74854f; // muted grass-green for unowned land
const BORDER = 0x243018; // dark, earthy tile edges
const SELECT = 0xffffff;
const HL_LAND = 0xffd54a; // yellow, move / capture on land
const HL_ATTACK = 0xff4d4d; // red, attack an enemy unit (melee / ranged)
const HL_SEA = 0xff8c3a; // orange, reachable by sea assault
const HOSTILE = 0xd42020; // deep red, a faction the player is at WAR with (subtle wash on their land + on their troops)
// a red, ~34%-opacity wash applied straight to an enemy-at-war figure's own pixels: R' = 0.66·R + 0.34 (and G,B
// scaled to 0.66). That is exactly "solid red overlaid at 0.34 alpha" on the character's silhouette, a red-glass
// tint, no ring and no heavy recolour. One shared instance, reused across every hostile sprite.
const hostileTint = new ColorMatrixFilter();
hostileTint.matrix = [0.66, 0, 0, 0, 0.34, 0, 0.66, 0, 0, 0, 0, 0, 0.66, 0, 0, 0, 0, 0, 1, 0];
// water depth + coast, drawn as concentric blobs (deep -> shallow -> foam -> beach) so hex clusters
// read as islands shoaling into the sea
const SEA_MID = 0x529ac6; // first step up from the deep-ocean background
const SHALLOW_FAR = 0x6fb2da; // mid shallows
const SHALLOW_NEAR = 0x9acbe9; // shallow water hugging the coast
const FOAM = 0xdaf2fc; // pale surf line where water meets sand
const COAST = 0xdcc793; // sandy beach ring
const TILE = 0.97; // region hex size (small gaps read as field boundaries)
// For each hex edge e (between corner e and e+1), the axial delta to the neighbour across it
// (pointy-top). Used to draw territory borders only on frontier edges.
const EDGE_DELTAS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [0, -1],
  [1, -1],
];

// Deterministic ±lightness jitter per hex so land reads as organic terrain, not a flat fill.
// Pure function of (q,r), no RNG, stays reproducible.
function terrainTint(color: number, q: number, r: number): number {
  const h = (Math.imul((q * 73856093) ^ (r * 19349663), 0x9e3779b1) >>> 0) % 1000;
  const f = 1 + (h / 1000 - 0.5) * 0.14; // ±7% brightness
  const cl = (c: number): number => Math.max(0, Math.min(255, Math.round(c * f)));
  return (cl((color >> 16) & 0xff) << 16) | (cl((color >> 8) & 0xff) << 8) | cl(color & 0xff);
}

// Deterministic 0..1 hash for placing terrain motifs (pure function of coords + slot).
function jitter(q: number, r: number, i: number): number {
  return ((Math.imul((q * 374761393) ^ (r * 668265263) ^ (i * 2246822519), 0x9e3779b1) >>> 0) % 1000) / 1000;
}

// Draw one small terrain motif for a resource type at (px,py), scaled by hex size s.
// res: 0 Gold, 1 Wood, 2 Stone, 3 Food (see RES_ICON in types.ts).
function drawMotif(g: Graphics, res: number, px: number, py: number, s: number): void {
  if (res === 1) {
    // pine tree: trunk + canopy
    g.rect(px - s * 0.018, py + s * 0.02, s * 0.036, s * 0.07).fill({ color: 0x6a4423 });
    g.poly([px, py - s * 0.17, px - s * 0.1, py + s * 0.03, px + s * 0.1, py + s * 0.03]).fill({ color: 0x2f6b34 });
    g.poly([px, py - s * 0.08, px - s * 0.08, py + s * 0.05, px + s * 0.08, py + s * 0.05]).fill({ color: 0x3d8043 });
  } else if (res === 2) {
    // mountain: grey peak with snow cap
    g.poly([px, py - s * 0.18, px - s * 0.14, py + s * 0.09, px + s * 0.14, py + s * 0.09]).fill({ color: 0x7c7f88 });
    g.poly([px, py - s * 0.18, px - s * 0.05, py - s * 0.05, px + s * 0.05, py - s * 0.05]).fill({ color: 0xecf0f4 });
  } else if (res === 3) {
    // crops: a small fan of wheat stalks
    for (let k = -1; k <= 1; k++) {
      g.moveTo(px + k * s * 0.06, py + s * 0.09).lineTo(px + k * s * 0.09, py - s * 0.09);
      g.stroke({ width: Math.max(1, s * 0.03), color: 0xd8bf45 });
    }
  } else {
    // gold: a low ochre mound with a bright fleck
    g.ellipse(px, py + s * 0.02, s * 0.13, s * 0.08).fill({ color: 0x9c7d2b });
    g.circle(px + s * 0.03, py - s * 0.02, s * 0.032).fill({ color: 0xffe27a });
  }
}

// White emblem for a unit type inside its chip (r = chip radius): sword / bow+arrow / horseshoe.
// Vector icons stay crisp at any zoom and read better than a letter.
function drawUnitIcon(g: Graphics, type: number, cx: number, cy: number, r: number): void {
  const w = Math.max(1.5, r * 0.2);
  const ink = 0xffffff;
  const arc = (rad: number, aFrom: number, aTo: number, ox = 0, oy = 0): number[] => {
    const p: number[] = [];
    const steps = 12;
    for (let i = 0; i <= steps; i++) {
      const a = ((aFrom + ((aTo - aFrom) * i) / steps) * Math.PI) / 180;
      p.push(cx + ox + rad * Math.cos(a), cy + oy + rad * Math.sin(a));
    }
    return p;
  };
  if (type === 0) {
    // Warrior: upright sword: long blade, short grip below the crossguard, a round pommel
    g.moveTo(cx, cy - r * 0.64).lineTo(cx, cy + r * 0.46).stroke({ width: w, color: ink, cap: 'round' });
    g.moveTo(cx - r * 0.3, cy + r * 0.26).lineTo(cx + r * 0.3, cy + r * 0.26).stroke({ width: w, color: ink, cap: 'round' });
    g.circle(cx, cy + r * 0.52, w * 0.6).fill({ color: ink });
  } else if (type === 1) {
    // Archer: bow (C opening right) + arrow
    g.poly(arc(r * 0.62, 118, 242, -r * 0.12), false).stroke({ width: w, color: ink, cap: 'round' });
    g.moveTo(cx - r * 0.5, cy).lineTo(cx + r * 0.58, cy).stroke({ width: w * 0.85, color: ink, cap: 'round' });
    g.moveTo(cx + r * 0.58, cy).lineTo(cx + r * 0.3, cy - r * 0.22).moveTo(cx + r * 0.58, cy).lineTo(cx + r * 0.3, cy + r * 0.22).stroke({ width: w * 0.85, color: ink, cap: 'round' });
  } else if (type === 2) {
    // Rider: chess-knight horse head (filled silhouette, the universal cavalry symbol)
    const s = r;
    g.poly(
      [
        cx - 0.16 * s, cy + 0.56 * s, // neck front, bottom
        cx + 0.34 * s, cy + 0.56 * s, // neck back, bottom
        cx + 0.24 * s, cy - 0.08 * s, // up the back of the neck
        cx + 0.06 * s, cy - 0.44 * s, // mane
        cx - 0.04 * s, cy - 0.56 * s, // ear
        cx - 0.14 * s, cy - 0.34 * s, // forehead
        cx - 0.46 * s, cy - 0.26 * s, // snout tip (points left)
        cx - 0.40 * s, cy - 0.04 * s, // under the snout
        cx - 0.12 * s, cy + 0.04 * s, // jaw / throat
        cx - 0.18 * s, cy + 0.56 * s, // back to neck front
      ],
      true,
    ).fill({ color: ink });
  } else {
    // Spearman: a long spear held upright: slender shaft with a leaf-shaped head at the top
    g.moveTo(cx, cy - r * 0.34).lineTo(cx, cy + r * 0.66).stroke({ width: w * 0.9, color: ink, cap: 'round' });
    g.poly([cx, cy - r * 0.78, cx - r * 0.22, cy - r * 0.3, cx + r * 0.22, cy - r * 0.3], true).fill({ color: ink });
    // a short crossbar where the head meets the shaft, so it reads as a spear, not an arrow
    g.moveTo(cx - r * 0.24, cy - r * 0.22).lineTo(cx + r * 0.24, cy - r * 0.22).stroke({ width: w * 0.7, color: ink, cap: 'round' });
  }
}

// A standalone anchor (ring + shank + stock + fluked arms), the universal harbor symbol, drawn crisp at
// any zoom. `width` lets the caller stroke it twice (a thick dark pass then a thin light pass) for a clean
// outlined look that pops on the water, with no badge/disc around it.
function drawAnchor(g: Graphics, cx: number, cy: number, r: number, color: number, width?: number): void {
  const w = width ?? Math.max(1.2, r * 0.17);
  g.circle(cx, cy - r * 0.72, r * 0.2).stroke({ width: w, color }); // top ring (rope hole)
  g.moveTo(cx, cy - r * 0.52).lineTo(cx, cy + r * 0.7); // shank (spine)
  g.moveTo(cx - r * 0.44, cy - r * 0.2).lineTo(cx + r * 0.44, cy - r * 0.2); // stock (crossbar)
  g.stroke({ width: w, color, cap: 'round' });
  g.moveTo(cx - r * 0.56, cy + r * 0.06); // fluked arms, a curved U across the bottom
  g.quadraticCurveTo(cx - r * 0.5, cy + r * 0.78, cx, cy + r * 0.72);
  g.quadraticCurveTo(cx + r * 0.5, cy + r * 0.78, cx + r * 0.56, cy + r * 0.06);
  g.stroke({ width: w, color, cap: 'round' });
  g.moveTo(cx - r * 0.56, cy + r * 0.06).lineTo(cx - r * 0.8, cy - r * 0.16); // fluke barbs (pointed tips)
  g.moveTo(cx + r * 0.56, cy + r * 0.06).lineTo(cx + r * 0.8, cy - r * 0.16);
  g.stroke({ width: w, color, cap: 'round' });
}

export interface HexMapView {
  /** `board` rewinds the tiles for a replay; `fleet` then says which boats were at sea at THAT point
   *  (the live voyage list belongs to the present, so a rewind must be told what to show). */
  render(g: GameState, board?: readonly RegionSnapshot[], fleet?: readonly Voyage[]): void;
  setSelected(id: RegionId | null, animated?: boolean): void;
  /** Advance the pulsing outline on the active-unit tile; returns true if it redrew (needs a render). */
  animateSelection(dtMs: number): boolean;
  setHighlights(land: ReadonlyArray<RegionId>, attack: ReadonlyArray<RegionId>, sea: ReadonlyArray<RegionId>): void;
  /** White hover ring over the move target under the cursor (null clears). Returns true if it changed. */
  setHoverTarget(id: RegionId | null): boolean;
  setSprites(store: SpriteStore): void;
  /** Mark a faction whose OWN units render with their race's War Regalia skin (null = nobody skinned). */
  setSkinnedFaction(faction: number | null): void;
  /** Voyage index whose boat sits within reach of a world point (boats float off-tile, pick() misses them). */
  boatAt(worldX: number, worldY: number): number | null;
  /** Curved course arrow from a voyage's boat to its destination beach (null clears it). */
  setVoyageArrow(index: number | null): void;
  /** Replay animation: show a unit sprite gliding fromId → toId; drive with updateMove(0..1).
   *  `arc` curves the glide along the voyage bow (sea landings), matching the course-arrow shape. */
  beginMove(fromId: RegionId, toId: RegionId, race: string, unitType: number, skinned?: boolean, arc?: boolean): void;
  /** Boarding animation: the unit walks from its tile down to its boat's sea position. */
  beginBoard(fromId: RegionId, destId: RegionId, race: string, unitType: number, skinned?: boolean): void;
  /** Landing animation: BOAT + passenger sail the course bow together; near the shore the boat fades
   *  away and the soldier steps onto the beach. Drive with updateSail(0..1), then endSail(). */
  beginSail(fromId: RegionId, destId: RegionId, race: string, unitType: number, ownerColor: number, skinned?: boolean, startAt?: { x: number; y: number }): void;
  /** World position of the boat on a given voyage leg (so a re-routed sail can start where it sits). */
  boatWorldOf(fromId: RegionId, destId: RegionId): { x: number; y: number } | null;
  updateSail(t: number): void;
  endSail(): void;
  updateMove(t: number): void;
  endMove(): void;
  /** Ranged attack: an arrow arcs fromId → toId; drive with updateArrow(0..1). The shooter never leaves its tile. */
  beginArrow(fromId: RegionId, toId: RegionId): void;
  updateArrow(t: number): void;
  endArrow(): void;
  /** Brief coloured highlight on a region (call clearFlash to remove). */
  flash(id: RegionId, color: number): void;
  clearFlash(): void;
  pick(worldX: number, worldY: number): RegionId | null;
  bounds(): { minX: number; minY: number; maxX: number; maxY: number };
  /** Place proper-name labels over each island (world coords); pass [] to clear. Set once, names don't change. */
  setIslandLabels(labels: ReadonlyArray<{ x: number; y: number; text: string }>): void;
  /** Screen-constant sizing + zoom fade for the island names (call whenever the camera changes). */
  setLabelZoom(camScale: number, baseScale: number): void;
}

export function buildHexMapView(layers: Layers, size: number, localSeat?: () => number): HexMapView {
  const fillG = new Graphics();
  layers.fills.addChild(fillG);
  const highlightG = new Graphics();
  layers.overlay.addChild(highlightG);
  const hoverG = new Graphics(); // the white "THIS is the tile you are about to click" ring, above the target highlights
  layers.overlay.addChild(hoverG);
  const unitBgG = new Graphics();
  layers.overlay.addChild(unitBgG);
  const spriteC = new Container();
  layers.overlay.addChild(spriteC);
  const flashG = new Graphics();
  layers.overlay.addChild(flashG);
  const animC = new Container();
  layers.overlay.addChild(animC);
  const voyArrowG = new Graphics(); // curved course arrow of a selected boat (above units, below the projectile)
  layers.overlay.addChild(voyArrowG);
  const arrowG = new Graphics(); // transient ranged-attack projectile, drawn above units
  layers.overlay.addChild(arrowG);
  const selG = new Graphics();
  layers.overlay.addChild(selG);
  const pulseG = new Graphics();
  layers.overlay.addChild(pulseG);
  // Island-name labels: anchored in world space but kept at a constant SCREEN size and faded out as
  // the camera closes in (setLabelZoom). Inserted at index 0 so every unit, health bar, badge and
  // effect draws OVER the text: names are wayfinding, never allowed to hide a piece someone reads.
  const labelC = new Container();
  layers.overlay.addChildAt(labelC, 0);

  const regionByKey = new Map<string, RegionId>();
  let regions: GameState['regions'] = [];
  let sprites: SpriteStore | null = null;
  let skinnedFaction: number | null = null; // this faction's own units use their race's War Regalia skin
  let lastGame: GameState | null = null;
  let mover: { sprite: Sprite; from: { x: number; y: number }; to: { x: number; y: number }; ctrl?: { x: number; y: number } } | null = null;
  let arrow: { from: { x: number; y: number }; to: { x: number; y: number } } | null = null;
  let selReg: RegionId | null = null; // the tile whose active-unit outline is pulsing
  let selPulse = 0; // 0..1 looping phase
  let selElapsed = 0; // ms since selection; the pulse settles after PULSE_MS so we stop re-rendering when idle
  let selSettled = false;
  const PULSE_MS = 2600;

  function outline(g: Graphics, reg: GameState['regions'][number], width: number, color: number): void {
    const { x, y } = axialToPixel(reg.q, reg.r, size);
    g.poly(hexCorners(x, y, size * 0.94));
    g.stroke({ width, color, alpha: 1 });
  }

  // Harbor marker: a little wooden pier + anchored dock on the tile's SEA-FACING edge, projecting out over
  // the water. Picks ONE sea edge (not an average, averaging cancels on isthmus tiles and lands the icon in
  // a weird corner) and uses a warm wood plate (high contrast on blue water) instead of a blue-on-blue disc.
  function drawHarborDock(reg: GameState['regions'][number], x: number, y: number): void {
    const corners = hexCorners(x, y, size * TILE);
    // choose the single most-visible sea-facing edge (frontmost = largest y offset)
    let bestX = 0;
    let bestY = 0;
    let bestScore = -Infinity;
    for (let e = 0; e < 6; e++) {
      const d = EDGE_DELTAS[e]!;
      if (regionByKey.get(hexKey(reg.q + d[0], reg.r + d[1])) != null) continue; // this edge borders land
      const mx = (corners[e * 2]! + corners[((e + 1) % 6) * 2]!) / 2 - x;
      const my = (corners[e * 2 + 1]! + corners[((e + 1) % 6) * 2 + 1]!) / 2 - y;
      if (my > bestScore) {
        bestScore = my;
        bestX = mx;
        bestY = my;
      }
    }
    let dx = 0;
    let dy = 0; // no sea edge at all (a legacy inland port): sit the anchor ON the tile, never over a neighbour
    if (bestScore > -Infinity) {
      const len = Math.hypot(bestX, bestY) || 1;
      dx = bestX / len;
      dy = bestY / len;
    }
    const hx = x + dx * size * 0.9; // the anchor sits on the coast edge, facing the sea
    const hy = y + dy * size * 0.9;
    // a plain, bold blue anchor, no disc, no pier. Two passes: a dark navy outline, then a blue fill on
    // top, so it stays crisp and high-contrast on the water.
    const ar = size * 0.32;
    drawAnchor(unitBgG, hx, hy, ar, 0x0c1c2e, size * 0.1); // dark navy outline / shadow
    drawAnchor(unitBgG, hx, hy, ar, 0x2f97e6, size * 0.052); // blue body
  }

  function render(g: GameState, board?: readonly RegionSnapshot[], fleet?: readonly Voyage[]): void {
    lastGame = g;
    // the LOCAL player's seat: online every client hydrates the SAME state (playerFaction is seat 0
    // for everyone), so anything perspective-based must key off the local seat instead
    const meSeat = localSeat ? localSeat() : g.playerFaction;
    regions = g.regions;
    regionByKey.clear();
    for (const reg of g.regions) regionByKey.set(hexKey(reg.q, reg.r), reg.id); // complete lookup up-front (harbor sea-edge detection needs it during the loop)
    // capitals that are part of a castle (a wall/gate touches them): they render as the KEEP of the
    // building instead of the plain capital star, and walls treat them as connectable castle parts
    const castleKeeps = new Set<RegionId>();
    for (const reg of g.regions) {
      if (!reg.isCapital) continue;
      // a walled-district lord's capital is the KEEP even when the rampart runs far from it
      if (g.factions[reg.owner]?.garrison) {
        castleKeeps.add(reg.id);
        continue;
      }
      for (const d of EDGE_DELTAS) {
        const nid = regionByKey.get(hexKey(reg.q + d[0], reg.r + d[1]));
        const t = nid != null ? g.regions[nid]!.terrain : undefined;
        if (t === 'wall' || t === 'gate') {
          castleKeeps.add(reg.id);
          break;
        }
      }
    }
    fillG.clear();
    unitBgG.clear();
    for (const child of spriteC.removeChildren()) child.destroy();

    // factions the player is currently at WAR with: their territory gets a faint red wash and their troops a
    // red threat aura, so an enemy reads at a glance on the map without opening the Diplomacy panel
    const hostile = new Set<number>();
    for (const [a, b] of g.wars) {
      if (a === meSeat) hostile.add(b);
      else if (b === meSeat) hostile.add(a);
    }

    // 1. water depth + coastline: batched unions of enlarged hexes, drawn far → near,
    //    so each island's hex cluster merges into a landmass ringed by shallows + beach.
    const halo = (mult: number, color: number): void => {
      for (const reg of g.regions) {
        const { x, y } = axialToPixel(reg.q, reg.r, size);
        fillG.poly(hexCorners(x, y, size * mult));
      }
      fillG.fill({ color });
    };
    halo(1.6, SEA_MID);
    halo(1.42, SHALLOW_FAR);
    halo(1.26, SHALLOW_NEAR);
    halo(1.15, FOAM);
    halo(1.1, COAST);

    // 2. region tiles on top of the land
    for (const reg of g.regions) {
      regionByKey.set(hexKey(reg.q, reg.r), reg.id);
      const { x, y } = axialToPixel(reg.q, reg.r, size);
      const snap = board ? board[reg.id] : undefined;
      const owner = snap ? snap.owner : reg.owner;
      const unit = snap ? snap.unit : reg.unit;
      const buildings = snap ? snap.buildings : reg.buildings;
      const pts = hexCorners(x, y, size * TILE);
      // every tile is natural terrain; ownership is a soft tint + a bold frontier border (drawn later),
      // so islands read as real land rather than flat colour blocks
      const color = terrainTint(NEUTRAL_COLOR, reg.q, reg.r);
      fillG.poly(pts);
      fillG.fill({ color });
      if (owner !== NEUTRAL) {
        fillG.poly(pts);
        fillG.fill({ color: g.factions[owner]?.color ?? NEUTRAL_COLOR, alpha: 0.3 });
        // enemy-at-war land: a faint red wash over the owner tint, marking hostile ground
        if (hostile.has(owner)) {
          fillG.poly(pts);
          fillG.fill({ color: HOSTILE, alpha: 0.17 });
        }
      }
      fillG.poly(pts);
      fillG.stroke({ width: 1, color: BORDER, alpha: 0.4 });
      // which geometric neighbours are parts of the same castle (adjacency was stripped, so this is
      // pure geometry): used to draw CONNECTED curtain walls so a castle reads as ONE building.
      const castleDirs = (): number[] => {
        const dirs: number[] = [];
        for (let e = 0; e < 6; e++) {
          const d = EDGE_DELTAS[e]!;
          const nid = regionByKey.get(hexKey(reg.q + d[0], reg.r + d[1]));
          if (nid == null) continue;
          const n = g.regions[nid]!;
          if (n.terrain === 'wall' || n.terrain === 'gate' || (n.isCapital && castleKeeps.has(nid))) dirs.push(e);
        }
        return dirs;
      };
      // ROAD: a dirt highway bending through the tile toward every connected road / gate / capital,
      // drawn before motifs (which are skipped) so the land maps read as a designed road network.
      if (reg.terrain === 'road') {
        const exits: Array<[number, number]> = [];
        for (let e = 0; e < 6; e++) {
          const d = EDGE_DELTAS[e]!;
          const nid = regionByKey.get(hexKey(reg.q + d[0], reg.r + d[1]));
          if (nid == null) continue;
          const n = g.regions[nid]!;
          if (n.terrain === 'road' || n.terrain === 'gate' || n.isCapital) {
            const corners2 = hexCorners(x, y, size * TILE);
            exits.push([(corners2[e * 2]! + corners2[((e + 1) % 6) * 2]!) / 2, (corners2[e * 2 + 1]! + corners2[((e + 1) % 6) * 2 + 1]!) / 2]);
          }
        }
        if (!exits.length) exits.push([x + size * 0.6, y]);
        for (const pass of [
          { w: size * 0.46, color: 0x6d5a3a, alpha: 0.9 }, // packed-earth bed
          { w: size * 0.3, color: 0x93794d, alpha: 1 }, // the trodden surface
        ]) {
          for (const [ex, ey] of exits) {
            fillG.moveTo(x, y).quadraticCurveTo((x + ex) / 2, (y + ey) / 2, ex, ey);
            fillG.stroke({ width: pass.w, color: pass.color, alpha: pass.alpha, cap: 'round' });
          }
        }
        // wheel ruts: two faint grooves along each arm
        for (const [ex, ey] of exits) {
          const ang = Math.atan2(ey - y, ex - x);
          const px2 = -Math.sin(ang) * size * 0.075;
          const py2 = Math.cos(ang) * size * 0.075;
          fillG.moveTo(x + px2, y + py2).lineTo(ex + px2, ey + py2);
          fillG.moveTo(x - px2, y - py2).lineTo(ex - px2, ey - py2);
          fillG.stroke({ width: Math.max(0.8, size * 0.035), color: 0x5c4a2e, alpha: 0.6 });
        }
      }
      // MOUNTAIN: an impassable massif. Big overlapping peaks fill the hex on a rocky base; no
      // connective bands (they read as trails between the peaks, which walls off the wrong idea).
      if (reg.terrain === 'mountain') {
        const rock = terrainTint(0x8a8378, reg.q, reg.r);
        fillG.poly(pts);
        fillG.fill({ color: rock });
        const peak = (px: number, py: number, s2: number, tone: number): void => {
          fillG.poly([px, py - s2 * 0.62, px - s2 * 0.5, py + s2 * 0.3, px + s2 * 0.5, py + s2 * 0.3]);
          fillG.fill({ color: tone });
          fillG.poly([px, py - s2 * 0.62, px - s2 * 0.16, py - s2 * 0.18, px + s2 * 0.13, py - s2 * 0.22]);
          fillG.fill({ color: 0xf0f3f7 }); // snowcap
          fillG.moveTo(px, py - s2 * 0.62).lineTo(px - s2 * 0.14, py + s2 * 0.3);
          fillG.stroke({ width: 1, color: 0x5f594f, alpha: 0.7 }); // ridgeline
        };
        peak(x - size * 0.24, y + size * 0.1, size * 0.66, 0x6f6a61);
        peak(x + size * 0.28, y + size * 0.16, size * 0.56, 0x7b756b);
        peak(x + size * 0.02, y - size * 0.14, size * 0.82, 0x847d72);
        continue; // scenery: nothing else ever renders here
      }
      // CASTLE WALL: a curtain segment of ONE larger building. Bands run edge-to-edge toward every
      // adjacent castle part (so they join up across tiles); junctions and endpoints grow a round
      // tower with a slate cone roof. No per-tile slab repetition.
      if (reg.terrain === 'wall') {
        const dirs = castleDirs();
        const corners2 = hexCorners(x, y, size * TILE);
        const edgeMid = (e: number): [number, number] => [(corners2[e * 2]! + corners2[((e + 1) % 6) * 2]!) / 2, (corners2[e * 2 + 1]! + corners2[((e + 1) % 6) * 2 + 1]!) / 2];
        // curtain bands: dark footing under a lighter walkway, merlon dots on top
        for (const pass of [
          { w: size * 0.62, color: 0x6f727d, alpha: 1 },
          { w: size * 0.44, color: 0xa9adb8, alpha: 1 },
        ]) {
          for (const e of dirs) {
            const [ex, ey] = edgeMid(e);
            fillG.moveTo(x, y).lineTo(ex, ey);
            fillG.stroke({ width: pass.w, color: pass.color, alpha: pass.alpha, cap: 'butt' });
          }
        }
        for (const e of dirs) {
          const [ex, ey] = edgeMid(e);
          for (const t of [0.3, 0.55, 0.8]) {
            const mx = x + (ex - x) * t;
            const my = y + (ey - y) * t;
            fillG.rect(mx - size * 0.07, my - size * 0.2, size * 0.14, size * 0.14);
            fillG.fill({ color: 0x878b96 });
          }
        }
        // a wall with a straight run keeps a low turret; corners, junctions and endpoints get the tower
        const straight = dirs.length === 2 && (dirs[1]! - dirs[0]!) === 3;
        const tr = straight ? size * 0.3 : size * 0.42;
        fillG.circle(x, y + size * 0.02, tr);
        fillG.fill({ color: 0x9a9da8 });
        fillG.circle(x, y + size * 0.02, tr);
        fillG.stroke({ width: 1.6, color: 0x4e525d, alpha: 0.9 });
        fillG.circle(x, y + size * 0.02, tr * 0.66);
        fillG.fill({ color: 0xb4b8c2 });
        // slate cone roof + finial: the château silhouette
        fillG.poly([x, y - tr * 1.7, x - tr * 0.95, y + size * 0.02 - tr * 0.28, x + tr * 0.95, y + size * 0.02 - tr * 0.28]);
        fillG.fill({ color: 0x5b6b8c });
        fillG.poly([x, y - tr * 1.7, x - tr * 0.95, y + size * 0.02 - tr * 0.28, x + tr * 0.95, y + size * 0.02 - tr * 0.28]);
        fillG.stroke({ width: 1.2, color: 0x39445c, alpha: 0.9 });
        fillG.circle(x, y - tr * 1.72, size * 0.045);
        fillG.fill({ color: 0xd9c26a });
        continue; // scenery: no motifs, no buildings, never a unit
      }
      if (reg.terrain === 'gate') {
        // barbican: the castle's mouth. Curtain stubs run toward the adjacent castle parts so the
        // gatehouse joins the walls seamlessly, then twin slate-roofed towers bridge the passage.
        const corners2 = hexCorners(x, y, size * TILE);
        for (const e of castleDirs()) {
          const ex = (corners2[e * 2]! + corners2[((e + 1) % 6) * 2]!) / 2;
          const ey = (corners2[e * 2 + 1]! + corners2[((e + 1) % 6) * 2 + 1]!) / 2;
          fillG.moveTo(x + (ex - x) * 0.34, y + (ey - y) * 0.34).lineTo(ex, ey);
          fillG.stroke({ width: size * 0.62, color: 0x6f727d, alpha: 1, cap: 'butt' });
          fillG.moveTo(x + (ex - x) * 0.34, y + (ey - y) * 0.34).lineTo(ex, ey);
          fillG.stroke({ width: size * 0.44, color: 0xa9adb8, alpha: 1, cap: 'butt' });
        }
        const gy = y - size * 0.18;
        const tower = (tx: number): void => {
          fillG.roundRect(tx, gy - size * 0.3, size * 0.26, size * 0.58, size * 0.05);
          fillG.fill({ color: 0x8f929e });
          fillG.roundRect(tx, gy - size * 0.3, size * 0.26, size * 0.58, size * 0.05);
          fillG.stroke({ width: 1.2, color: 0x4e525d, alpha: 0.9 });
          // slate cone cap matching the curtain towers, so the whole building shares one roofline
          fillG.poly([tx + size * 0.13, gy - size * 0.56, tx - size * 0.03, gy - size * 0.28, tx + size * 0.29, gy - size * 0.28]);
          fillG.fill({ color: 0x5b6b8c });
          fillG.poly([tx + size * 0.13, gy - size * 0.56, tx - size * 0.03, gy - size * 0.28, tx + size * 0.29, gy - size * 0.28]);
          fillG.stroke({ width: 1, color: 0x39445c, alpha: 0.9 });
        };
        tower(x - size * 0.48);
        tower(x + size * 0.22);
        fillG.rect(x - size * 0.24, gy - size * 0.26, size * 0.48, size * 0.2); // the bridge over the gate
        fillG.fill({ color: 0x9a9da8 });
        fillG.rect(x - size * 0.24, gy - size * 0.26, size * 0.48, size * 0.2);
        fillG.stroke({ width: 1.2, color: 0x4e525d, alpha: 0.9 });
        // the arched passage, dark, with a raised portcullis hint
        fillG.moveTo(x - size * 0.14, gy + size * 0.28);
        fillG.lineTo(x - size * 0.14, gy + size * 0.02);
        fillG.quadraticCurveTo(x, gy - size * 0.14, x + size * 0.14, gy + size * 0.02);
        fillG.lineTo(x + size * 0.14, gy + size * 0.28);
        fillG.closePath();
        fillG.fill({ color: 0x23262d });
        for (let bar = -1; bar <= 1; bar++) {
          fillG.moveTo(x + bar * size * 0.07, gy - size * 0.04).lineTo(x + bar * size * 0.07, gy + size * 0.1);
        }
        fillG.stroke({ width: 1, color: 0x6b6f7a, alpha: 0.9 });
      }
      // terrain motifs keyed to the island's two resources (skip capitals, the star sits there).
      // Per-tile the count and which of the two resources appears vary deterministically, so
      // forest/mine patches scatter naturally across the island instead of one tiled pattern.
      if (!reg.isCapital && !reg.terrain) {
        const res = g.islands[reg.island]?.resources;
        if (res) {
          // candidate spots in the upper 2/3 of the hex (unit chip owns the bottom)
          const spots: ReadonlyArray<[number, number]> = [
            [-0.24, -0.04],
            [0.22, -0.14],
            [0.02, 0.04],
          ];
          const count = jitter(reg.q, reg.r, 3) < 0.4 ? 2 : 3;
          for (let si = 0; si < count; si++) {
            const spot = spots[si]!;
            const kind = jitter(reg.q, reg.r, si + 11) < 0.55 ? res[0]! : res[1]!;
            const jx = (jitter(reg.q, reg.r, si) - 0.5) * 0.12;
            const jy = (jitter(reg.q, reg.r, si + 7) - 0.5) * 0.1;
            drawMotif(fillG, kind, x + (spot[0] + jx) * size, y + (spot[1] + jy) * size, size);
          }
        }
      }
      if (reg.isCapital && castleKeeps.has(reg.id)) {
        // the KEEP: the castle's heart, one tall donjon flanked by twin turrets, sharing the slate
        // roofline of the curtain towers so keep + walls + gate read as a single château
        const kw = size * 0.3;
        const turret = (tx: number, s2: number): void => {
          fillG.roundRect(tx - s2 * 0.5, y - size * 0.18 - s2 * 1.2, s2, s2 * 1.5, s2 * 0.16);
          fillG.fill({ color: 0x9a9da8 });
          fillG.roundRect(tx - s2 * 0.5, y - size * 0.18 - s2 * 1.2, s2, s2 * 1.5, s2 * 0.16);
          fillG.stroke({ width: 1.1, color: 0x4e525d, alpha: 0.9 });
          fillG.poly([tx, y - size * 0.18 - s2 * 2.0, tx - s2 * 0.62, y - size * 0.18 - s2 * 1.16, tx + s2 * 0.62, y - size * 0.18 - s2 * 1.16]);
          fillG.fill({ color: 0x5b6b8c });
        };
        turret(x - size * 0.42, size * 0.22);
        turret(x + size * 0.42, size * 0.22);
        fillG.roundRect(x - kw, y - size * 0.72, kw * 2, size * 0.94, size * 0.06); // the donjon body
        fillG.fill({ color: 0xa9adb8 });
        fillG.roundRect(x - kw, y - size * 0.72, kw * 2, size * 0.94, size * 0.06);
        fillG.stroke({ width: 1.4, color: 0x4e525d, alpha: 0.95 });
        fillG.rect(x - size * 0.07, y - size * 0.38, size * 0.14, size * 0.26); // a tall window
        fillG.fill({ color: 0x2b2f38 });
        fillG.poly([x, y - size * 1.2, x - kw * 1.18, y - size * 0.68, x + kw * 1.18, y - size * 0.68]);
        fillG.fill({ color: 0x5b6b8c });
        fillG.poly([x, y - size * 1.2, x - kw * 1.18, y - size * 0.68, x + kw * 1.18, y - size * 0.68]);
        fillG.stroke({ width: 1.2, color: 0x39445c, alpha: 0.95 });
        // the lord's banner hangs from the roofline in the OWNER's colour: siege progress at a glance
        const lord = g.factions[owner]?.color ?? 0x888888;
        fillG.rect(x - size * 0.05, y - size * 1.06, size * 0.1, size * 0.3);
        fillG.fill({ color: lord });
        fillG.circle(x, y - size * 1.22, size * 0.05);
        fillG.fill({ color: 0xd9c26a });
      } else if (reg.isCapital) {
        fillG.star(x, y - size * 0.28, 5, size * 0.24, size * 0.11);
        fillG.fill({ color: 0xffffff, alpha: 0.9 });
        fillG.star(x, y - size * 0.28, 5, size * 0.24, size * 0.11);
        fillG.stroke({ width: 1.2, color: BORDER, alpha: 0.9 });
        // the player's own capital flies a banner in their colour, so "which one is me?" is never in doubt
        if (owner === meSeat) {
          const poleTop = y - size * 1.12;
          const poleBot = y - size * 0.5;
          const px = x - size * 0.02;
          const col = g.factions[meSeat]?.color ?? 0xffffff;
          unitBgG.roundRect(px - size * 0.04, poleTop, size * 0.08, poleBot - poleTop, size * 0.03);
          unitBgG.fill({ color: 0x3b2a1a });
          const pennant = (gg: Graphics): void => {
            gg.moveTo(px + size * 0.03, poleTop + size * 0.02);
            gg.lineTo(px + size * 0.5, poleTop + size * 0.17);
            gg.lineTo(px + size * 0.03, poleTop + size * 0.32);
            gg.closePath();
          };
          pennant(unitBgG);
          unitBgG.fill({ color: col });
          pennant(unitBgG);
          unitBgG.stroke({ width: size * 0.045, color: 0xffe08a, alpha: 0.95 });
        }
      }
      // the campaign objective: a gold target ring so the prize is findable at a glance from any zoom
      if (g.objective?.region === reg.id) {
        const held = owner === meSeat;
        unitBgG.poly(hexCorners(x, y, size * 0.99));
        unitBgG.stroke({ width: size * 0.11, color: held ? 0x6ce07a : 0xffd24a, alpha: 0.95 });
        unitBgG.poly(hexCorners(x, y, size * 0.99));
        unitBgG.stroke({ width: size * 0.04, color: 0x3a2a08, alpha: 0.8 });
        const ty = y - size * 1.18;
        unitBgG.circle(x, ty, size * 0.2);
        unitBgG.fill({ color: held ? 0x6ce07a : 0xffd24a });
        unitBgG.circle(x, ty, size * 0.2);
        unitBgG.stroke({ width: size * 0.05, color: 0x3a2a08, alpha: 0.9 });
        unitBgG.circle(x, ty, size * 0.09);
        unitBgG.fill({ color: 0x3a2a08 });
      }
      // Buildings sit in FIXED corners, one per type, so two of them can never stack on each other:
      // the Market takes the upper LEFT, the Barracks the upper RIGHT, scenery walls the top centre.
      // (The Harbor is excluded: it is drawn as a dock on the sea-facing edge below, facing the water.)
      const BUILD_SLOT: ReadonlyArray<readonly [number, number]> = [
        [-0.36, -0.1], // Market
        [0.36, -0.1], // Barracks
        [0, -0.34], // Wall (scenario scenery)
        [0, 0], // Harbor (unused here)
      ];
      for (const btype of buildings) {
        if (btype === BUILDING.Harbor) continue;
        const slot = BUILD_SLOT[btype] ?? [0, -0.1];
        const bx = x + slot[0]! * size;
        const byBase = y + slot[1]! * size;
        const btex = sprites ? sprites.building(BUILDING_SPRITE[btype]) : null;
        if (btex) {
          const bsp = new Sprite(btex);
          bsp.anchor.set(0.5, 1);
          bsp.scale.set((size * 0.46) / btex.height);
          bsp.position.set(bx, byBase);
          spriteC.addChild(bsp);
        } else {
          unitBgG.circle(bx, byBase - size * 0.14, size * 0.15);
          unitBgG.fill({ color: BUILDING_BADGE[btype] ?? 0xffffff, alpha: 0.95 });
          unitBgG.circle(bx, byBase - size * 0.14, size * 0.15);
          unitBgG.stroke({ width: 1.4, color: 0x0d1017, alpha: 0.7 });
        }
      }
      // Harbor → a dock + anchor on the coast, facing the open sea (drawn after the tile body it launches from)
      if (buildings.includes(BUILDING.Harbor)) drawHarborDock(reg, x, y);
      if (unit) {
        const ownerColor = g.factions[unit.owner]?.color ?? 0x888888;
        const cx = x;
        const cy = y + size * 0.3;
        const chip = size * 0.32;
        const race = g.factions[unit.owner]?.race ?? '';
        const tex = sprites?.unit(race, unit.type, unit.owner === skinnedFaction) ?? null;
        if (tex) {
          // owner-coloured base pad so the figure reads on any tile, then the standing sprite (full colour,
          // a spent unit is shown by its move badge, not by fading, so the map never looks washed out)
          const baseY = cy + size * 0.36;
          unitBgG.ellipse(cx, baseY, chip * 0.92, chip * 0.34);
          unitBgG.fill({ color: 0x0d1017, alpha: 0.32 });
          unitBgG.ellipse(cx, baseY, chip * 0.92, chip * 0.34);
          unitBgG.stroke({ width: 2, color: ownerColor, alpha: 1 });
          const sp = new Sprite(tex);
          sp.anchor.set(0.5, 0.92);
          sp.scale.set((size * 0.98) / tex.height);
          sp.position.set(cx, baseY + size * 0.05);
          // enemy-at-war: a red, low-opacity wash laid straight over the figure's own pixels (a red-glass tint),
          // so the soldier itself reads red without a ring or a heavy recolour
          if (hostile.has(unit.owner)) sp.filters = [hostileTint];
          spriteC.addChild(sp);
        } else {
          unitBgG.circle(cx, cy, chip);
          unitBgG.fill({ color: 0x11161f, alpha: 0.92 });
          unitBgG.circle(cx, cy, chip);
          unitBgG.stroke({ width: 2.4, color: ownerColor });
          drawUnitIcon(unitBgG, unit.type, cx, cy, chip);
          // enemy-at-war (vector fallback): a red wash over the whole chip
          if (hostile.has(unit.owner)) {
            unitBgG.circle(cx, cy, chip);
            unitBgG.fill({ color: HOSTILE, alpha: 0.34 });
          }
        }
        // health pips (only when hurt) so damaged units read at a glance
        const mh = unitMaxHp(unit.type);
        if (unit.hp < mh) {
          const pipW = (chip * 1.7) / mh;
          const pipH = size * 0.11;
          const y0 = cy - size * 0.62;
          const x0 = cx - (pipW * mh) / 2;
          for (let i = 0; i < mh; i++) {
            unitBgG.rect(x0 + i * pipW + 0.6, y0, pipW - 1.2, pipH);
            unitBgG.fill({ color: i < unit.hp ? 0x54d66a : 0x39121a });
            unitBgG.rect(x0 + i * pipW + 0.6, y0, pipW - 1.2, pipH);
            unitBgG.stroke({ width: 1, color: 0x0a0d12, alpha: 0.85 });
          }
        }
        // move-point badge above a player's own unit's head: green while it can still act (e.g. Rider 2/2 or 1/2),
        // grey once spent (0/1, 0/2). (Live view only, not during the AI replay.) Each Text owns its style so safe.
        if (!board && unit.owner === meSeat) {
          const spent = !canStillAct(unit); // an archer keeps its green badge while its arrow is unspent
          const label = new Text({
            text: `${Math.max(0, unit.movesLeft)}/${unitMoves(unit.type)}`,
            style: { fontFamily: 'Nunito, Segoe UI, sans-serif', fontSize: 22, fontWeight: '800', fill: spent ? 0xcabf9e : 0xa6ff7e, stroke: { color: 0x0a0d08, width: 4 } },
          });
          label.anchor.set(0.5);
          label.scale.set((size * 0.42) / 22);
          label.position.set(cx, cy - size * 0.86);
          spriteC.addChild(label);
        }
      }
    }

    // 3. territory borders: a bold faction-coloured line only on frontier edges (owned land meeting a
    //    different owner, neutral land, or sea), reads as a strategy-map border instead of a grid.
    for (const reg of g.regions) {
      const rOwner = board ? board[reg.id]!.owner : reg.owner;
      if (rOwner === NEUTRAL) continue;
      const fc = g.factions[rOwner]?.color ?? 0x888888;
      const { x, y } = axialToPixel(reg.q, reg.r, size);
      const corners = hexCorners(x, y, size * TILE);
      let drew = false;
      for (let e = 0; e < 6; e++) {
        const d = EDGE_DELTAS[e]!;
        const nid = regionByKey.get(hexKey(reg.q + d[0], reg.r + d[1]));
        const nOwner = nid != null ? (board ? board[nid]!.owner : g.regions[nid]!.owner) : NEUTRAL;
        if (nOwner === rOwner) continue; // interior edge → no border
        fillG.moveTo(corners[e * 2]!, corners[e * 2 + 1]!).lineTo(corners[((e + 1) % 6) * 2]!, corners[((e + 1) % 6) * 2 + 1]!);
        drew = true;
      }
      if (drew) fillG.stroke({ width: 3.4, color: fc, cap: 'round', join: 'round' });
    }

    // 4. boats at sea: every voyage renders as a small ship floating one tile off its origin's coast,
    // aimed at its destination, crewed by the embarked unit. Re-emitted every render (immediate mode).
    // A rewind draws the fleet the caller hands it (the replay tracks which boats were at sea at that
    // moment); the live view draws the real voyage list.
    const boats = board ? (fleet ?? []) : g.voyages;
    for (let vi = 0; vi < boats.length; vi++) {
      const v = boats[vi]!;
      const pos = boatWorldPos(g, v.from, v.dest);
      if (!pos) continue;
      const col = g.factions[v.owner]?.color ?? 0x888888;
      const bx = pos.x;
      const by = pos.y;
      // wake ripples so the ship reads as ON the water
      unitBgG.ellipse(bx, by + size * 0.16, size * 0.62, size * 0.2);
      unitBgG.stroke({ width: 1.6, color: 0xdaf2fc, alpha: 0.5 });
      // hull: a wide wooden crescent with a deck line and an owner-coloured gunwale
      unitBgG.moveTo(bx - size * 0.52, by - size * 0.08);
      unitBgG.quadraticCurveTo(bx, by + size * 0.46, bx + size * 0.52, by - size * 0.08);
      unitBgG.closePath();
      unitBgG.fill({ color: 0x6a4a2c });
      unitBgG.moveTo(bx - size * 0.52, by - size * 0.08).lineTo(bx + size * 0.52, by - size * 0.08);
      unitBgG.stroke({ width: size * 0.09, color: 0x3b2a1a });
      unitBgG.moveTo(bx - size * 0.52, by - size * 0.08).lineTo(bx + size * 0.52, by - size * 0.08);
      unitBgG.stroke({ width: size * 0.035, color: col });
      // mast + swept sail + a pennant in the owner's colour
      unitBgG.rect(bx - size * 0.025, by - size * 0.78, size * 0.05, size * 0.72);
      unitBgG.fill({ color: 0x3b2a1a });
      unitBgG.moveTo(bx + size * 0.03, by - size * 0.74);
      unitBgG.quadraticCurveTo(bx + size * 0.42, by - size * 0.48, bx + size * 0.03, by - size * 0.16);
      unitBgG.closePath();
      unitBgG.fill({ color: 0xf3e7cb });
      unitBgG.moveTo(bx - size * 0.03, by - size * 0.78);
      unitBgG.lineTo(bx - size * 0.3, by - size * 0.7);
      unitBgG.lineTo(bx - size * 0.03, by - size * 0.62);
      unitBgG.closePath();
      unitBgG.fill({ color: col });
      // wounded crews must read at a glance, ours and theirs alike: the same HP pips a unit wears on land
      const mh = unitMaxHp(v.unit.type);
      if (v.unit.hp < mh) {
        const pipW = (size * 0.55) / mh;
        const pipH = size * 0.11;
        const y0 = by - size * 1.02;
        const x0 = bx - (pipW * mh) / 2;
        for (let i = 0; i < mh; i++) {
          unitBgG.rect(x0 + i * pipW + 0.6, y0, pipW - 1.2, pipH);
          unitBgG.fill({ color: i < v.unit.hp ? 0x54d66a : 0x39121a });
          unitBgG.rect(x0 + i * pipW + 0.6, y0, pipW - 1.2, pipH);
          unitBgG.stroke({ width: 1, color: 0x0a0d12, alpha: 0.85 });
        }
      }
      // the passenger, small, standing amidships (sprite when loaded, else a tiny owner chip)
      const race = g.factions[v.owner]?.race ?? '';
      const tex = sprites?.unit(race, v.unit.type, v.owner === skinnedFaction) ?? null;
      if (tex) {
        const sp = new Sprite(tex);
        sp.anchor.set(0.5, 1);
        sp.scale.set((size * 0.6) / tex.height);
        sp.position.set(bx - size * 0.16, by - size * 0.02);
        if (hostile.has(v.owner)) sp.filters = [hostileTint];
        spriteC.addChild(sp);
      } else {
        unitBgG.circle(bx - size * 0.16, by - size * 0.28, size * 0.16);
        unitBgG.fill({ color: 0x11161f, alpha: 0.92 });
        unitBgG.circle(bx - size * 0.16, by - size * 0.28, size * 0.16);
        unitBgG.stroke({ width: 1.8, color: col });
      }
    }
  }

  function setSprites(store: SpriteStore): void {
    sprites = store;
    if (lastGame) render(lastGame);
  }

  function setSkinnedFaction(faction: number | null): void {
    skinnedFaction = faction;
    if (lastGame) render(lastGame);
  }

  // pixel anchor where a unit sprite stands in a region (matches the static draw position)
  function unitPos(id: RegionId): { x: number; y: number } {
    const reg = regions[id];
    if (!reg) return { x: 0, y: 0 };
    const { x, y } = axialToPixel(reg.q, reg.r, size);
    return { x, y: y + size * 0.71 };
  }

  // Where a boat floats: on a real WATER hex right beside its port, the one pointing most towards the
  // destination. (Walking a straight line to the destination and taking the first empty hex failed on
  // mainland crossings, where the line runs overland the whole way and the boat ended up parked on a
  // field.) A port is coastal by definition, so an adjacent sea hex always exists.
  function boatWorldPos(g: GameState, fromId: RegionId, destId: RegionId): { x: number; y: number } | null {
    const from = g.regions[fromId];
    const dest = g.regions[destId];
    if (!from || !dest) return null;
    const a = axialToPixel(from.q, from.r, size);
    const b = axialToPixel(dest.q, dest.r, size);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    let best: { x: number; y: number } | null = null;
    let bestScore = -Infinity;
    // anchor on the water the boat is actually SAILING: a port can touch two bodies (sea one side,
    // a lake the other), and pure bearing used to park the sprite on the wrong one. Prefer neighbour
    // water belonging to a body that also laps the destination's shore; bearing breaks ties.
    const destWaters = shoreWatersOf(g, dest);
    for (const n of hexNeighbors(from.q, from.r)) {
      if (regionByKey.get(hexKey(n.q, n.r)) != null) continue; // land, not water
      const c = axialToPixel(n.q, n.r, size);
      const body = waterBodyAt(g, n.q, n.r);
      const onCourse = body !== undefined && destWaters.has(body);
      const score = (onCourse ? 1e6 : 0) + ((c.x - a.x) * dx + (c.y - a.y) * dy) / len;
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    }
    if (best) return best;
    // a landlocked port (only possible on a legacy save): step out along the course and take the first
    // hex with no land at all, so the boat still ends up on water rather than in a field
    for (let step = 1; step <= 12; step++) {
      const px = a.x + (dx / len) * size * 0.9 * step;
      const py = a.y + (dy / len) * size * 0.9 * step;
      const cell = pixelToAxial(px, py, size);
      if (regionByKey.get(hexKey(cell.q, cell.r)) == null) {
        const c = axialToPixel(cell.q, cell.r, size);
        return { x: c.x, y: c.y };
      }
    }
    return null; // nowhere at sea to draw it: skip the boat rather than beach it on land
  }

  function boatAt(worldX: number, worldY: number): number | null {
    if (!lastGame) return null;
    for (let i = 0; i < lastGame.voyages.length; i++) {
      const v = lastGame.voyages[i]!;
      const p = boatWorldPos(lastGame, v.from, v.dest);
      if (p && Math.hypot(worldX - p.x, worldY - p.y) <= size * 0.75) return i;
    }
    return null;
  }

  // control point for the course bow: the midpoint pushed perpendicular, so the line reads as a sailing
  // route rather than a ruler line. Bow size scales with distance but stays gentle.
  function courseCtrl(x1: number, y1: number, x2: number, y2: number): { x: number; y: number } {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const bow = Math.min(size * 2.2, len * 0.22);
    return { x: (x1 + x2) / 2 - (dy / len) * bow, y: (y1 + y2) / 2 + (dx / len) * bow };
  }

  function setVoyageArrow(index: number | null): void {
    voyArrowG.clear();
    if (index == null || !lastGame) return;
    const v = lastGame.voyages[index];
    if (!v) return;
    const from = boatWorldPos(lastGame, v.from, v.dest);
    const destReg = lastGame.regions[v.dest];
    if (!from || !destReg) return;
    const ownerCol = lastGame.factions[v.owner]?.color ?? HL_SEA; // the course flies its captain's colours
    const to = axialToPixel(destReg.q, destReg.r, size);
    const c = courseCtrl(from.x, from.y, to.x, to.y);
    const at = (t: number): [number, number] => [
      (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * c.x + t * t * to.x,
      (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * c.y + t * t * to.y,
    ];
    // dashed bezier, two passes (dark under-stroke + sea-orange core) so it reads over open water.
    // Dashes run all the way to the arrowhead's BASE (t≈0.95) and the final dash is forced to close
    // exactly there, so the line and the head always meet instead of leaving a floating tip.
    const STEPS = 32;
    const T_MAX = 0.95; // the head occupies the remainder
    for (const pass of [
      { width: Math.max(3, size * 0.16), color: 0x1a2733, alpha: 0.85 },
      { width: Math.max(1.6, size * 0.08), color: ownerCol, alpha: 1 },
    ]) {
      for (let s = 0; s < STEPS; s += 2) {
        const t0 = (s / STEPS) * T_MAX;
        const t1 = Math.min(T_MAX, ((s + 1) / STEPS) * T_MAX);
        const [ax, ay] = at(t0);
        const [bx2, by2] = at(t1);
        voyArrowG.moveTo(ax, ay).lineTo(bx2, by2);
      }
      const [cx2, cy2] = at(T_MAX - 0.03); // the closing dash: always touch the head base
      const [dx2, dy2] = at(T_MAX);
      voyArrowG.moveTo(cx2, cy2).lineTo(dx2, dy2);
      voyArrowG.stroke({ width: pass.width, color: pass.color, alpha: pass.alpha, cap: 'round' });
    }
    // arrowhead on the beach end, aligned with the curve's final tangent
    const [t1x, t1y] = at(0.94);
    const dx = to.x - t1x;
    const dy = to.y - t1y;
    const alen = Math.hypot(dx, dy) || 1;
    const ux = dx / alen;
    const uy = dy / alen;
    const hw = size * 0.34;
    voyArrowG.moveTo(to.x, to.y);
    voyArrowG.lineTo(to.x - ux * hw - uy * hw * 0.6, to.y - uy * hw + ux * hw * 0.6);
    voyArrowG.lineTo(to.x - ux * hw + uy * hw * 0.6, to.y - uy * hw - ux * hw * 0.6);
    voyArrowG.closePath();
    voyArrowG.fill({ color: ownerCol });
    voyArrowG.moveTo(to.x, to.y);
    voyArrowG.lineTo(to.x - ux * hw - uy * hw * 0.6, to.y - uy * hw + ux * hw * 0.6);
    voyArrowG.lineTo(to.x - ux * hw + uy * hw * 0.6, to.y - uy * hw - ux * hw * 0.6);
    voyArrowG.closePath();
    voyArrowG.stroke({ width: 1.4, color: 0x1a2733, alpha: 0.85 });
  }

  function beginMove(fromId: RegionId, toId: RegionId, race: string, unitType: number, skinned = false, arc = false): void {
    endMove();
    const tex = sprites?.unit(race, unitType, skinned) ?? null;
    if (!tex) return; // no art → caller falls back to an instant state change
    const sp = new Sprite(tex);
    sp.anchor.set(0.5, 0.92);
    sp.scale.set((size * 0.98) / tex.height);
    const from = unitPos(fromId);
    sp.position.set(from.x, from.y);
    animC.addChild(sp);
    const to = unitPos(toId);
    // sea landings glide along the same gentle bow the course arrow draws, instead of a ruler line
    mover = { sprite: sp, from, to, ...(arc ? { ctrl: courseCtrl(from.x, from.y, to.x, to.y) } : {}) };
  }

  // Boarding: the unit walks from its tile down onto its boat's sea position (a short straight glide).
  function beginBoard(fromId: RegionId, destId: RegionId, race: string, unitType: number, skinned = false): void {
    endMove();
    if (!lastGame) return;
    const tex = sprites?.unit(race, unitType, skinned) ?? null;
    const to = boatWorldPos(lastGame, fromId, destId);
    if (!tex || !to) return; // no art → caller falls back to an instant state change
    const sp = new Sprite(tex);
    sp.anchor.set(0.5, 0.92);
    sp.scale.set((size * 0.98) / tex.height);
    const from = unitPos(fromId);
    sp.position.set(from.x, from.y);
    animC.addChild(sp);
    mover = { sprite: sp, from, to: { x: to.x, y: to.y + size * 0.18 } };
  }

  // --- the landing voyage: boat + passenger as one container, sailing the bow, docking at the beach ---
  let sail: { c: Container; boat: Graphics; sprite: Sprite; from: { x: number; y: number }; to: { x: number; y: number }; ctrl: { x: number; y: number } } | null = null;

  // the little ship, drawn at local (0,0) so the animation container can carry it anywhere
  function drawBoatInto(gg: Graphics, col: number): void {
    gg.ellipse(0, size * 0.16, size * 0.62, size * 0.2);
    gg.stroke({ width: 1.6, color: 0xdaf2fc, alpha: 0.5 });
    gg.moveTo(-size * 0.52, -size * 0.08);
    gg.quadraticCurveTo(0, size * 0.46, size * 0.52, -size * 0.08);
    gg.closePath();
    gg.fill({ color: 0x6a4a2c });
    gg.moveTo(-size * 0.52, -size * 0.08).lineTo(size * 0.52, -size * 0.08);
    gg.stroke({ width: size * 0.09, color: 0x3b2a1a });
    gg.moveTo(-size * 0.52, -size * 0.08).lineTo(size * 0.52, -size * 0.08);
    gg.stroke({ width: size * 0.035, color: col });
    gg.rect(-size * 0.025, -size * 0.78, size * 0.05, size * 0.72);
    gg.fill({ color: 0x3b2a1a });
    gg.moveTo(size * 0.03, -size * 0.74);
    gg.quadraticCurveTo(size * 0.42, -size * 0.48, size * 0.03, -size * 0.16);
    gg.closePath();
    gg.fill({ color: 0xf3e7cb });
    gg.moveTo(-size * 0.03, -size * 0.78);
    gg.lineTo(-size * 0.3, -size * 0.7);
    gg.lineTo(-size * 0.03, -size * 0.62);
    gg.closePath();
    gg.fill({ color: col });
  }

  function beginSail(fromId: RegionId, destId: RegionId, race: string, unitType: number, ownerColor: number, skinned = false, startAt?: { x: number; y: number }): void {
    endSail();
    if (!lastGame) return;
    const tex = sprites?.unit(race, unitType, skinned) ?? null;
    const from = startAt ?? boatWorldPos(lastGame, fromId, destId);
    if (!tex || !from) return; // no art → the caller falls back to the plain glide
    const c = new Container();
    const boat = new Graphics();
    drawBoatInto(boat, ownerColor);
    const sp = new Sprite(tex);
    sp.anchor.set(0.5, 1);
    sp.scale.set((size * 0.6) / tex.height);
    sp.position.set(-size * 0.16, -size * 0.02);
    c.addChild(boat, sp);
    c.position.set(from.x, from.y);
    animC.addChild(c);
    const to = unitPos(destId);
    sail = { c, boat, sprite: sp, from, to, ctrl: courseCtrl(from.x, from.y, to.x, to.y) };
  }

  function updateSail(t: number): void {
    if (!sail) return;
    const e = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2; // ease in-out along the bow
    const u = 1 - e;
    const x = u * u * sail.from.x + 2 * u * e * sail.ctrl.x + e * e * sail.to.x;
    const y = u * u * sail.from.y + 2 * u * e * sail.ctrl.y + e * e * sail.to.y;
    sail.c.position.set(x, y);
    // the last stretch is the DOCKING: the boat melts away while the soldier stands up onto the sand
    const dock = Math.max(0, (e - 0.78) / 0.22);
    sail.boat.alpha = 1 - dock;
    sail.sprite.scale.set(((size * (0.6 + 0.38 * dock)) / (sail.sprite.texture.height || 1)));
    sail.sprite.position.set(-size * 0.16 * (1 - dock), -size * 0.02 * (1 - dock));
  }

  function endSail(): void {
    if (sail) {
      sail.c.destroy({ children: true });
      sail = null;
    }
  }

  function updateMove(t: number): void {
    if (!mover) return;
    const e = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2; // ease in-out
    if (mover.ctrl) {
      // quadratic bezier through the bow control point (sea landings)
      const u = 1 - e;
      const x = u * u * mover.from.x + 2 * u * e * mover.ctrl.x + e * e * mover.to.x;
      const y = u * u * mover.from.y + 2 * u * e * mover.ctrl.y + e * e * mover.to.y;
      mover.sprite.position.set(x, y);
      return;
    }
    // a smooth slide across the ground (no hop) so the unit glides rather than jumps
    const x = mover.from.x + (mover.to.x - mover.from.x) * e;
    const y = mover.from.y + (mover.to.y - mover.from.y) * e;
    mover.sprite.position.set(x, y);
  }

  function endMove(): void {
    if (mover) {
      mover.sprite.destroy();
      mover = null;
    }
  }

  // --- ranged attack: an arrow flies from the shooter's tile to the target and lands (the shooter stays put) ---
  function beginArrow(fromId: RegionId, toId: RegionId): void {
    arrow = { from: unitPos(fromId), to: unitPos(toId) };
  }
  function updateArrow(t: number): void {
    arrowG.clear();
    if (!arrow) return;
    const { from, to } = arrow;
    const x = from.x + (to.x - from.x) * t;
    const y = from.y + (to.y - from.y) * t - Math.sin(t * Math.PI) * size * 0.45; // a shallow lobbed arc
    // face the direction of travel (tangent of the arc), so the arrowhead points where it flies
    const dxTo = to.x - from.x;
    const dyTo = to.y - from.y - Math.cos(t * Math.PI) * Math.PI * size * 0.45;
    const ang = Math.atan2(dyTo, dxTo);
    const dx = Math.cos(ang);
    const dy = Math.sin(ang);
    const px = -dy;
    const py = dx;
    const half = size * 0.34; // shaft half-length
    const hw = size * 0.17; // arrowhead size
    const tipX = x + dx * half;
    const tipY = y + dy * half;
    const tailX = x - dx * half;
    const tailY = y - dy * half;
    // shaft: a dark line with a bright core so it reads over both green land and blue sea
    arrowG.moveTo(tailX, tailY).lineTo(tipX, tipY).stroke({ width: Math.max(2, size * 0.1), color: 0x201206 });
    arrowG.moveTo(tailX, tailY).lineTo(tipX, tipY).stroke({ width: Math.max(0.8, size * 0.04), color: 0xf3e7cb });
    // arrowhead (a filled triangle at the tip)
    arrowG
      .moveTo(tipX + dx * hw, tipY + dy * hw)
      .lineTo(tipX - dx * hw * 0.35 + px * hw, tipY - dy * hw * 0.35 + py * hw)
      .lineTo(tipX - dx * hw * 0.35 - px * hw, tipY - dy * hw * 0.35 - py * hw)
      .fill({ color: 0x201206 });
    // fletching: two short red flights at the tail
    arrowG.moveTo(tailX, tailY).lineTo(tailX - dx * hw + px * hw * 0.85, tailY - dy * hw + py * hw * 0.85).stroke({ width: Math.max(1, size * 0.045), color: 0xd8452e });
    arrowG.moveTo(tailX, tailY).lineTo(tailX - dx * hw - px * hw * 0.85, tailY - dy * hw - py * hw * 0.85).stroke({ width: Math.max(1, size * 0.045), color: 0xd8452e });
  }
  function endArrow(): void {
    arrowG.clear();
    arrow = null;
  }

  function flash(id: RegionId, color: number): void {
    const reg = regions[id];
    if (!reg) return;
    const { x, y } = axialToPixel(reg.q, reg.r, size);
    flashG.poly(hexCorners(x, y, size * TILE));
    flashG.fill({ color, alpha: 0.4 });
  }

  function clearFlash(): void {
    flashG.clear();
  }

  function setSelected(id: RegionId | null, animated = false): void {
    selG.clear();
    pulseG.clear();
    selReg = animated ? id : null;
    selPulse = 0;
    selElapsed = 0;
    selSettled = false;
    if (id == null) return;
    const reg = regions[id];
    if (reg) outline(selG, reg, 3, SELECT);
  }

  // A "sonar ping", two hex rings expanding out of the active tile and fading, so the player can see
  // at a glance which unit is up. Runs only while an active (not-yet-moved) player unit is selected, and
  // settles after a couple of seconds so a left-selected unit doesn't pin a 60fps render loop forever.
  function animateSelection(dtMs: number): boolean {
    if (selReg == null) return false;
    const reg = regions[selReg];
    if (!reg) return false;
    selElapsed += dtMs;
    if (selElapsed > PULSE_MS) {
      if (selSettled) return false; // already faded → nothing more to draw, stop pinning the render loop
      selSettled = true;
      pulseG.clear(); // one last frame to settle to the static outline
      return true;
    }
    selPulse = (selPulse + dtMs / 1000) % 1;
    const { x, y } = axialToPixel(reg.q, reg.r, size);
    pulseG.clear();
    for (const off of [0, 0.5]) {
      const e = (selPulse + off) % 1;
      pulseG.poly(hexCorners(x, y, size * (0.9 + 0.5 * e)));
      pulseG.stroke({ width: Math.max(0.5, 3.5 - 2.5 * e), color: SELECT, alpha: 0.85 * (1 - e) });
    }
    return true;
  }

  // the tile the cursor is on RIGHT NOW, among the lit move targets: a bright white ring so "which
  // tile am I about to click" is never a guess (player report: misclicks next to the intended tile)
  let hoverId: RegionId | null = null;
  function setHoverTarget(id: RegionId | null): boolean {
    if (id === hoverId) return false;
    hoverId = id;
    hoverG.clear();
    const reg = id != null ? regions[id] : null;
    if (reg) {
      const { x, y } = axialToPixel(reg.q, reg.r, size);
      hoverG.poly(hexCorners(x, y, size * 0.94));
      hoverG.stroke({ width: 7, color: 0xffffff, alpha: 0.35 }); // soft outer glow
      hoverG.poly(hexCorners(x, y, size * 0.9));
      hoverG.stroke({ width: 3, color: 0xffffff, alpha: 0.95 }); // crisp inner ring
    }
    return true;
  }

  function setHighlights(land: ReadonlyArray<RegionId>, attack: ReadonlyArray<RegionId>, sea: ReadonlyArray<RegionId>): void {
    setHoverTarget(null); // fresh target set: any old hover ring belongs to the previous selection
    highlightG.clear();
    for (const id of land) {
      const reg = regions[id];
      if (reg) outline(highlightG, reg, 3.2, HL_LAND);
    }
    for (const id of attack) {
      const reg = regions[id];
      if (reg) outline(highlightG, reg, 3.4, HL_ATTACK);
    }
    for (const id of sea) {
      const reg = regions[id];
      if (reg) outline(highlightG, reg, 3.2, HL_SEA);
    }
  }

  function pick(worldX: number, worldY: number): RegionId | null {
    const { q, r } = pixelToAxial(worldX, worldY, size);
    return regionByKey.get(hexKey(q, r)) ?? null;
  }

  function bounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const reg of regions) {
      const { x, y } = axialToPixel(reg.q, reg.r, size);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    if (!isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    return { minX: minX - size, minY: minY - size, maxX: maxX + size, maxY: maxY + size };
  }

  function setIslandLabels(labels: ReadonlyArray<{ x: number; y: number; text: string }>): void {
    for (const child of labelC.removeChildren()) child.destroy();
    for (const l of labels) {
      const t = new Text({
        text: l.text,
        // rendered at 2× so the raster stays crisp when the camera zooms in
        resolution: 2,
        style: {
          fontFamily: 'Cinzel, Georgia, serif',
          fontSize: 20,
          fontWeight: '700',
          letterSpacing: 1.5,
          fill: 0xf3e4b6, // warm parchment gold
          stroke: { color: 0x1a1206, width: 5 }, // dark outline so it reads over land or sea
          dropShadow: { color: 0x000000, alpha: 0.5, blur: 4, distance: 2, angle: Math.PI / 2 },
        },
      });
      t.anchor.set(0.5, 1); // sit the baseline just above the island
      t.position.set(l.x, l.y);
      t.alpha = 0.92;
      labelC.addChild(t);
    }
  }

  /** Names are wayfinding for the OVERVIEW: hold them at a constant on-screen size (they must not
   *  balloon over the board as the camera closes in) and fade them out entirely once the player is
   *  down at fighting zoom, so no piece, bar or camp is ever hidden behind a word. */
  function setLabelZoom(camScale: number, baseScale: number): void {
    if (!labelC.children.length || baseScale <= 0 || camScale <= 0) return;
    const f = Math.min(1.15, baseScale / camScale); // constant screen size; a whisker of growth at min zoom
    const zoomIn = camScale / baseScale; // 1 = the whole map fits the window
    // fade EARLY (player-tuned): names belong to the overview only, so they start dimming the moment
    // the camera leaves the fit view and are gone well before fighting zoom
    const alpha = zoomIn <= 1.1 ? 0.92 : Math.max(0, 0.92 * (1 - (zoomIn - 1.1) / 0.35)); // gone by ~1.45x
    labelC.visible = alpha > 0.02;
    if (!labelC.visible) return;
    for (const t of labelC.children) {
      t.scale.set(f);
      t.alpha = alpha;
    }
  }

  const boatWorldOf = (fromId: RegionId, destId: RegionId): { x: number; y: number } | null => (lastGame ? boatWorldPos(lastGame, fromId, destId) : null);

  return { render, setSelected, animateSelection, setHighlights, setHoverTarget, setSprites, setSkinnedFaction, boatAt, boatWorldOf, setVoyageArrow, beginMove, beginBoard, beginSail, updateSail, endSail, updateMove, endMove, beginArrow, updateArrow, endArrow, flash, clearFlash, pick, bounds, setIslandLabels, setLabelZoom };
}
